/**
 * useAdminProviders Hooks
 * React Query hooks for provider management with optimistic updates
 * 
 * Implements T111 and T112:
 * - useAdminProviders: List providers with pagination and filters
 * - useUpdateProvider: Update provider mutation
 * - useDeleteProvider: Delete provider mutation
 * - usePendingProvidersCount: Get count of pending providers
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import { adminProvidersApi } from '../services/adminProvidersApi';
import type { PaginatedResponse } from '../types/pagination.type';
import type {
  AdminProvider,
  CreateProviderPayload,
  UpdateProviderPayload,
  ProviderListParams,
} from '../types/admin-provider.type';

// Query keys for cache management
export const providerKeys = {
  all: ['admin', 'providers'] as const,
  lists: () => [...providerKeys.all, 'list'] as const,
  list: (params: ProviderListParams) => [...providerKeys.lists(), params] as const,
  details: () => [...providerKeys.all, 'detail'] as const,
  detail: (id: string) => [...providerKeys.details(), id] as const,
  full: (id: string) => [...providerKeys.details(), id, 'full'] as const,
  pendingCount: () => [...providerKeys.all, 'pending-count'] as const,
};

/**
 * Hook to fetch paginated providers list
 * 
 * @param params - Query parameters (page, pageSize, search, filters)
 * @returns Query result with paginated providers
 * 
 * Features:
 * - 30s stale time to reduce API calls
 * - keepPreviousData for smooth pagination
 * - Total count reflects filtered results
 * 
 * @example
 * const { data, isLoading, error } = useAdminProviders({
 *   page: 1,
 *   pageSize: 50,
 *   search: 'Best Care',
 *   status: 'approved'
 * });
 */
export function useAdminProviders(
  params: ProviderListParams = {},
): UseQueryResult<PaginatedResponse<AdminProvider>, Error> {
  return useQuery({
    queryKey: providerKeys.list(params),
    queryFn: () => adminProvidersApi.listProviders(params),
    staleTime: 30000, // 30 seconds
    placeholderData: (previousData) => previousData, // Keep previous data during refetch
  });
}

/**
 * Hook to fetch single provider by ID
 * 
 * @param id - Provider ID
 * @returns Query result with provider details
 */
export function useAdminProvider(
  id: string,
): UseQueryResult<AdminProvider, Error> {
  return useQuery({
    queryKey: providerKeys.detail(id),
    queryFn: () => adminProvidersApi.getProvider(id),
    enabled: !!id,
  });
}

/**
 * Hook to fetch full provider data for editing
 * 
 * @param id - Provider ID
 * @returns Query result with complete provider data including all relationships
 * 
 * Features:
 * - Fetches complete provider data with user, categories, specialties, expertise, etc.
 * - Used by provider editor for editing existing providers
 * - Includes user_id and kinde_id for display
 * - 10s stale time (editor data should be fresh)
 * 
 * @example
 * const { data: provider, isLoading } = useProviderFull(providerId);
 * 
 * if (isLoading) return <Skeleton />;
 * return <ProviderEditorPage provider={provider} />;
 */
export function useProviderFull(
  id: string | undefined,
): UseQueryResult<AdminProvider, Error> {
  return useQuery({
    queryKey: providerKeys.full(id || ''),
    queryFn: () => adminProvidersApi.getProviderFull(id || ''),
    enabled: !!id,
    staleTime: 10000, // 10 seconds - editor data should be relatively fresh
  });
}

/**
 * Hook to fetch count of pending providers (for badge)
 * 
 * @returns Query result with pending count
 * 
 * Features:
 * - 60s stale time (count doesn't change frequently)
 * - Used for Overview tab badge
 */
export function usePendingProvidersCount(): UseQueryResult<number, Error> {
  return useQuery({
    queryKey: providerKeys.pendingCount(),
    queryFn: adminProvidersApi.getPendingProvidersCount,
    staleTime: 60000, // 60 seconds
  });
}

/**
 * Hook to create new provider
 * 
 * @returns Mutation function and state
 * 
 * Features:
 * - Creates user account in Kinde IDP first
 * - Creates database user record
 * - Creates provider profile with relationships
 * - Syncs custom properties to Kinde
 * - Invalidates list queries to show new provider
 * 
 * @example
 * const createProvider = useCreateProvider();
 * 
 * await createProvider.mutateAsync({
 *   user: {
 *     email: 'john@example.com',
 *     firstName: 'John',
 *     lastName: 'Doe',
 *   },
 *   provider: {
 *     companyName: 'Best Care Services',
 *   },
 *   categoryIds: ['cat-123'],
 * });
 */
export function useCreateProvider(): UseMutationResult<
  AdminProvider,
  Error,
  CreateProviderPayload
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: adminProvidersApi.createProvider,
    
    // On success - invalidate and refetch lists
    onSuccess: (data) => {
      // Add new provider to detail cache
      queryClient.setQueryData(providerKeys.detail(data.id), data);
      
      // Invalidate all lists to show new provider
      queryClient.invalidateQueries({ queryKey: providerKeys.lists() });
      
      // Invalidate pending count if created as pending
      if (data.vettingStatus === 'PENDING') {
        queryClient.invalidateQueries({ queryKey: providerKeys.pendingCount() });
      }
    },
  });
}

/**
 * Hook to update existing provider
 * 
 * @returns Mutation function and state
 * 
 * Features:
 * - Optimistic update (immediate UI update)
 * - Rollback on error
 * - Updates cache with new data
 * 
 * @example
 * const updateProvider = useUpdateProvider();
 * 
 * await updateProvider.mutateAsync({
 *   id: 'provider_123',
 *   payload: { companyName: 'New Name' }
 * });
 */
export function useUpdateProvider(): UseMutationResult<
  AdminProvider,
  Error,
  { id: string; payload: UpdateProviderPayload }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }) => adminProvidersApi.updateProvider(id, payload),
    
    // Optimistic update
    onMutate: async ({ id, payload }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: providerKeys.detail(id) });

      // Snapshot previous value
      const previousProvider = queryClient.getQueryData<AdminProvider>(
        providerKeys.detail(id),
      );

      // Optimistically update cache with safe field merging
      if (previousProvider) {
        const { companyName, ...providerUpdates } = payload.provider || {};

        // Build the updated provider with safe type merging
        const updatedProvider: AdminProvider = {
          ...previousProvider,
          // Merge user fields if provided
          user: payload.user ? {
            ...previousProvider.user,
            ...payload.user,
          } : previousProvider.user,
          // Merge provider fields if provided
          ...providerUpdates,
          companyName: companyName ?? previousProvider.companyName,
          // Note: media, services, and complex arrays are skipped in optimistic update
          // They will be properly updated on success from server response
        };
        
        queryClient.setQueryData<AdminProvider>(providerKeys.detail(id), updatedProvider);
      }

      return { previousProvider };
    },
    
    // Rollback on error
    onError: (_error, { id }, context) => {
      if (context?.previousProvider) {
        queryClient.setQueryData(providerKeys.detail(id), context.previousProvider);
      }
    },
    
    // Refetch on success
    onSuccess: (data, { id }) => {
      // Update detail cache with server response
      queryClient.setQueryData(providerKeys.detail(id), data);
      
      // Invalidate lists to show updated provider
      queryClient.invalidateQueries({ queryKey: providerKeys.lists() });
      
      // Invalidate pending count if status changed
      queryClient.invalidateQueries({ queryKey: providerKeys.pendingCount() });
    },
  });
}

/**
 * Hook to update provider email (syncs with Kinde)
 *
 * @returns Mutation function and state
 *
 * @example
 * const updateEmail = useUpdateProviderEmail();
 * await updateEmail.mutateAsync({ id: 'provider_123', email: 'new@example.com' });
 */
export function useUpdateProviderEmail(): UseMutationResult<
  void,
  Error,
  { id: string; email: string }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, email }) => adminProvidersApi.updateProviderEmail(id, email),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: providerKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: providerKeys.lists() });
    },
  });
}

/**
 * Hook to soft-delete provider
 * 
 * @returns Mutation function and state
 * 
 * Features:
 * - Optimistic removal from lists
 * - Rollback on error
 * - Invalidates all related queries
 * 
 * @example
 * const deleteProvider = useDeleteProvider();
 * 
 * await deleteProvider.mutateAsync('provider_123');
 */
export function useDeleteProvider(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: adminProvidersApi.softDeleteProvider,
    
    // Optimistic update - remove from lists
    onMutate: async (id) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: providerKeys.lists() });

      // Snapshot all list queries
      const previousLists = queryClient.getQueriesData<
        PaginatedResponse<AdminProvider>
      >({
        queryKey: providerKeys.lists(),
      });

      // Optimistically remove from all cached lists
      queryClient.setQueriesData<PaginatedResponse<AdminProvider>>(
        { queryKey: providerKeys.lists() },
        (old) => {
          if (!old) return old;
          
          return {
            ...old,
            items: old.items.filter((provider) => provider.id !== id),
            meta: {
              ...old.meta,
              total: old.meta.total - 1,
            },
          };
        },
      );

      return { previousLists };
    },
    
    // Rollback on error
    onError: (_error, _id, context) => {
      if (context?.previousLists) {
        context.previousLists.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    
    // Refetch on success
    onSuccess: (_data, id) => {
      // Remove detail cache
      queryClient.removeQueries({ queryKey: providerKeys.detail(id) });
      
      // Invalidate all lists to get fresh totals
      queryClient.invalidateQueries({ queryKey: providerKeys.lists() });
      
      // Invalidate pending count
      queryClient.invalidateQueries({ queryKey: providerKeys.pendingCount() });
    },
  });
}
